import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { getAuthToken } from '../utils/auth';

const FollowUpManager = ({ department, leadId, quotationId, customerName, customerPhone, customerEmail, showOnlyForm = false, onSuccess, leads = [] }) => {
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(showOnlyForm);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'today', 'overdue', 'completed'
  const [selectedLeadForFollowUp, setSelectedLeadForFollowUp] = useState(null);
  const [showLeadSelector, setShowLeadSelector] = useState(false);
  
  const [formData, setFormData] = useState({
    followUpDate: '',
    followUpTime: '10:00',
    priority: 'medium',
    subject: '',
    notes: '',
    purpose: 'general'
  });

  useEffect(() => {
    console.log('🔍 FollowUpManager - Leads received:', leads?.length || 0, leads);
    if (!showOnlyForm) {
      fetchFollowUps();
    }
  }, [department, activeTab, showOnlyForm, leads]);

  const fetchFollowUps = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      
      let url = '';
      if (activeTab === 'today') {
        url = '/follow-up/manager/today';
      } else if (activeTab === 'overdue') {
        url = '/follow-up/manager/overdue';
      } else if (activeTab === 'completed') {
        url = `/follow-up/manager/my-followups?status=completed&department=${department}`;
      } else {
        url = `/follow-up/manager/department/${department}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setFollowUps(response.data.followUps || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching follow-ups:', error);
      setLoading(false);
    }
  };

  const handleAddFollowUp = async (e) => {
    e.preventDefault();
    
    try {
      const token = getAuthToken();
      
      // Try to get user from different possible localStorage keys
      let user = null;
      const employeeInfo = localStorage.getItem('employeeInfo');
      const companyInfo = localStorage.getItem('companyInfo');
      const userInfo = localStorage.getItem('userInfo');
      const legacyUser = localStorage.getItem('user');
      
      if (employeeInfo) {
        user = JSON.parse(employeeInfo);
      } else if (companyInfo) {
        user = JSON.parse(companyInfo);
      } else if (userInfo) {
        user = JSON.parse(userInfo);
      } else if (legacyUser) {
        user = JSON.parse(legacyUser);
      } else {
        user = {};
      }
      
      console.log('🔍 User from localStorage:', user);
      console.log('🔍 Selected lead:', selectedLeadForFollowUp);
      
      const userId = user._id || user.id;
      const userName = user.name || user.username || user.teamMemberName || user.companyName || 'Unknown';
      
      if (!userId) {
        alert('❌ User ID not found. Please logout and login again.');
        return;
      }
      
      const followUpData = {
        leadId: leadId || selectedLeadForFollowUp?._id,
        quotationId: quotationId,
        department: department,
        assignedTo: userId,
        assignedToName: userName,
        followUpDate: formData.followUpDate,
        followUpTime: formData.followUpTime,
        priority: formData.priority,
        subject: formData.subject,
        notes: formData.notes,
        purpose: formData.purpose,
        customerName: customerName || selectedLeadForFollowUp?.customerName,
        customerPhone: customerPhone || selectedLeadForFollowUp?.contactNumber,
        customerEmail: customerEmail || selectedLeadForFollowUp?.email
      };
      
      console.log('📤 Sending follow-up data:', followUpData);

      await axios.post(
        '/follow-up/manager/create',
        followUpData,
        { headers: { Authorization: `Bearer ${token}` }}
      );

      alert('✅ Follow-up created successfully!');
      setShowAddForm(false);
      setSelectedLeadForFollowUp(null);
      setFormData({
        followUpDate: '',
        followUpTime: '10:00',
        priority: 'medium',
        subject: '',
        notes: '',
        purpose: 'general'
      });
      
      if (onSuccess) {
        onSuccess();
      }
      
      if (!showOnlyForm) {
        fetchFollowUps();
      }
    } catch (error) {
      console.error('Error creating follow-up:', error);
      alert('❌ Error creating follow-up: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleStatusUpdate = async (followUpId, status, completionNotes = '') => {
    try {
      const token = getAuthToken();
      
      await axios.put(
        `/follow-up/manager/${followUpId}/status`,
        { status, completionNotes },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      alert('✅ Follow-up updated successfully!');
      fetchFollowUps();
    } catch (error) {
      console.error('Error updating follow-up:', error);
      alert('❌ Error updating follow-up: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleReschedule = async (followUpId) => {
    const newDate = prompt('Enter new date (YYYY-MM-DD):');
    const newTime = prompt('Enter new time (HH:MM):', '10:00');
    
    if (newDate) {
      try {
        const token = getAuthToken();
        
        await axios.put(
          `/follow-up/manager/${followUpId}/reschedule`,
          { followUpDate: newDate, followUpTime: newTime },
          { headers: { Authorization: `Bearer ${token}` }}
        );

        alert('✅ Follow-up rescheduled successfully!');
        fetchFollowUps();
      } catch (error) {
        console.error('Error rescheduling follow-up:', error);
        alert('❌ Error rescheduling follow-up: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleDelete = async (followUpId) => {
    if (window.confirm('Are you sure you want to delete this follow-up?')) {
      try {
        const token = getAuthToken();
        
        await axios.delete(
          `/follow-up/manager/${followUpId}`,
          { headers: { Authorization: `Bearer ${token}` }}
        );

        alert('✅ Follow-up deleted successfully!');
        fetchFollowUps();
      } catch (error) {
        console.error('Error deleting follow-up:', error);
        alert('❌ Error deleting follow-up: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'low': return 'bg-green-100 text-green-700 border-green-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      case 'rescheduled': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const isOverdue = (followUpDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fDate = new Date(followUpDate);
    fDate.setHours(0, 0, 0, 0);
    return fDate < today;
  };

  // If showOnlyForm is true, only render the form
  if (showOnlyForm) {
    return (
      <form onSubmit={handleAddFollowUp} className="space-y-4">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <p className="text-sm text-gray-700 mb-2">
            <strong>Customer:</strong> {customerName}
          </p>
          {customerPhone && (
            <p className="text-sm text-gray-700 mb-2">
              <strong>Phone:</strong> {customerPhone}
            </p>
          )}
          {customerEmail && (
            <p className="text-sm text-gray-700">
              <strong>Email:</strong> {customerEmail}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Follow-up Date *</label>
            <input
              type="date"
              required
              value={formData.followUpDate}
              onChange={(e) => setFormData({ ...formData, followUpDate: e.target.value })}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Time</label>
            <input
              type="time"
              value={formData.followUpTime}
              onChange={(e) => setFormData({ ...formData, followUpTime: e.target.value })}
              className="w-full border rounded px-3 py-2"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Priority *</label>
            <select
              required
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="w-full border rounded px-3 py-2"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Purpose</label>
            <select
              value={formData.purpose}
              onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
              className="w-full border rounded px-3 py-2"
            >
              <option value="general">General</option>
              <option value="quotation_follow_up">Quotation Follow-up</option>
              <option value="payment_reminder">Payment Reminder</option>
              <option value="booking_confirmation">Booking Confirmation</option>
              <option value="vendor_approval">Vendor Approval</option>
              <option value="document_collection">Document Collection</option>
              <option value="feedback">Feedback</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Subject *</label>
          <input
            type="text"
            required
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            className="w-full border rounded px-3 py-2"
            placeholder="e.g., Follow-up on quotation approval"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            className="w-full border rounded px-3 py-2"
            rows="4"
            placeholder="Additional notes..."
          />
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <button
            type="submit"
            className="px-6 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 font-medium"
          >
            ✅ Create Follow-up
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          📅 Follow-up Manager - {department === 'sales' ? 'Sales' : 'Reservation'}
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => {
              if (leads && leads.length > 0) {
                setShowLeadSelector(!showLeadSelector);
              } else {
                alert('No leads available. Please add leads first from "My Leads" tab.');
              }
            }}
            disabled={!leads || leads.length === 0}
            className={`px-4 py-2 rounded ${
              leads && leads.length > 0
                ? 'bg-orange-600 text-white hover:bg-orange-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            title={leads && leads.length > 0 ? 'Create follow-up for a specific lead' : 'No leads available'}
          >
            📋 Create for Lead {leads && leads.length > 0 && `(${leads.length})`}
          </button>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            {showAddForm ? '✕ Cancel' : '➕ Add Follow-up'}
          </button>
        </div>
      </div>

      {/* Lead Selector */}
      {showLeadSelector && leads && leads.length > 0 && (
        <div className="mb-6 bg-orange-50 p-4 rounded-lg border-2 border-orange-200">
          <h3 className="text-lg font-semibold mb-3">📋 Select a Lead to Create Follow-up</h3>
          <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
            {leads.map(lead => (
              <button
                key={lead._id}
                onClick={() => {
                  setSelectedLeadForFollowUp(lead);
                  setShowLeadSelector(false);
                  setShowAddForm(true);
                  setFormData({
                    ...formData,
                    subject: `Follow-up for ${lead.customerName} - ${lead.destination || 'Tour Package'}`
                  });
                }}
                className="text-left p-3 bg-white border rounded hover:bg-orange-50 hover:border-orange-400 transition-colors"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-900">{lead.customerName}</p>
                    <p className="text-sm text-gray-600">
                      {lead.destination || 'No destination'} • {lead.contactNumber}
                    </p>
                  </div>
                  <span className="text-orange-600">→</span>
                </div>
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowLeadSelector(false)}
            className="mt-3 text-sm text-gray-600 hover:text-gray-900"
          >
            ✕ Cancel
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'all'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          All ({followUps.length})
        </button>
        <button
          onClick={() => setActiveTab('today')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'today'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          📅 Today
        </button>
        <button
          onClick={() => setActiveTab('overdue')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'overdue'
              ? 'border-b-2 border-red-600 text-red-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          🚨 Overdue
        </button>
        <button
          onClick={() => setActiveTab('completed')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'completed'
              ? 'border-b-2 border-green-600 text-green-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          ✅ Completed
        </button>
      </div>

      {/* Add Follow-up Form */}
      {showAddForm && (
        <form onSubmit={handleAddFollowUp} className="bg-blue-50 p-4 rounded-lg mb-6 border-2 border-blue-200">
          <h3 className="text-lg font-semibold mb-4">
            ➕ Add New Follow-up
            {selectedLeadForFollowUp && (
              <span className="text-sm font-normal text-gray-600 ml-2">
                for {selectedLeadForFollowUp.customerName}
              </span>
            )}
          </h3>
          
          {selectedLeadForFollowUp && (
            <div className="bg-white p-3 rounded mb-4 border border-blue-300">
              <p className="text-sm text-gray-700 mb-1">
                <strong>Customer:</strong> {selectedLeadForFollowUp.customerName}
              </p>
              <p className="text-sm text-gray-700 mb-1">
                <strong>Phone:</strong> {selectedLeadForFollowUp.contactNumber}
              </p>
              <p className="text-sm text-gray-700 mb-1">
                <strong>Email:</strong> {selectedLeadForFollowUp.email}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Destination:</strong> {selectedLeadForFollowUp.destination || 'N/A'}
              </p>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Follow-up Date *</label>
              <input
                type="date"
                required
                value={formData.followUpDate}
                onChange={(e) => setFormData({ ...formData, followUpDate: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Time</label>
              <input
                type="time"
                value={formData.followUpTime}
                onChange={(e) => setFormData({ ...formData, followUpTime: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Priority *</label>
              <select
                required
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full border rounded px-3 py-2"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Purpose</label>
              <select
                value={formData.purpose}
                onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                className="w-full border rounded px-3 py-2"
              >
                <option value="general">General</option>
                <option value="quotation_follow_up">Quotation Follow-up</option>
                <option value="payment_reminder">Payment Reminder</option>
                <option value="booking_confirmation">Booking Confirmation</option>
                <option value="vendor_approval">Vendor Approval</option>
                <option value="document_collection">Document Collection</option>
                <option value="feedback">Feedback</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Subject *</label>
            <input
              type="text"
              required
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              className="w-full border rounded px-3 py-2"
              placeholder="e.g., Follow-up on quotation approval"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full border rounded px-3 py-2"
              rows="3"
              placeholder="Additional notes..."
            />
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              ✅ Create Follow-up
            </button>
          </div>
        </form>
      )}

      {/* Follow-ups List */}
      {loading ? (
        <div className="text-center py-8">
          <p className="text-gray-600">Loading follow-ups...</p>
        </div>
      ) : followUps.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-600">No follow-ups found</p>
          <p className="text-sm text-gray-500 mt-2">Click "Add Follow-up" to create one</p>
        </div>
      ) : (
        <div className="space-y-4">
          {followUps.map((followUp) => (
            <div
              key={followUp._id}
              className={`border-2 rounded-lg p-4 ${
                isOverdue(followUp.followUpDate) && followUp.status === 'pending'
                  ? 'border-red-300 bg-red-50'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{followUp.subject}</h3>
                    <span className={`text-xs px-2 py-1 rounded ${getPriorityColor(followUp.priority)}`}>
                      {followUp.priority.toUpperCase()}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${getStatusColor(followUp.status)}`}>
                      {followUp.status}
                    </span>
                  </div>
                  
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>
                      <strong>📅 Date:</strong> {formatDate(followUp.followUpDate)}
                      {followUp.followUpTime && ` at ${followUp.followUpTime}`}
                      {isOverdue(followUp.followUpDate) && followUp.status === 'pending' && (
                        <span className="ml-2 text-red-600 font-bold">⚠️ OVERDUE</span>
                      )}
                    </p>
                    {followUp.customerName && (
                      <p><strong>👤 Customer:</strong> {followUp.customerName}</p>
                    )}
                    {followUp.customerPhone && (
                      <p><strong>📞 Phone:</strong> {followUp.customerPhone}</p>
                    )}
                    {followUp.purpose && (
                      <p><strong>🎯 Purpose:</strong> {followUp.purpose.replace(/_/g, ' ')}</p>
                    )}
                    {followUp.notes && (
                      <p className="mt-2"><strong>📝 Notes:</strong> {followUp.notes}</p>
                    )}
                    {followUp.completionNotes && (
                      <p className="mt-2 text-green-700">
                        <strong>✅ Completion Notes:</strong> {followUp.completionNotes}
                      </p>
                    )}
                  </div>
                </div>

                {followUp.status === 'pending' && (
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => {
                        const notes = prompt('Enter completion notes:');
                        if (notes !== null) {
                          handleStatusUpdate(followUp._id, 'completed', notes);
                        }
                      }}
                      className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                      title="Mark as completed"
                    >
                      ✅ Complete
                    </button>
                    <button
                      onClick={() => handleReschedule(followUp._id)}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                      title="Reschedule"
                    >
                      📅 Reschedule
                    </button>
                    <button
                      onClick={() => handleDelete(followUp._id)}
                      className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </div>

              <div className="text-xs text-gray-500 mt-2 pt-2 border-t">
                Created by {followUp.createdByName} on {formatDate(followUp.createdAt)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FollowUpManager;
